import { useState } from 'react';

import { Grid, TableCell, TableSortLabel } from '@mui/material';
import { type ColumnResizeDirection, flexRender, type Header } from '@tanstack/react-table';
import cx from 'classnames';

import { BodySmallBold } from 'components/typography/Typography';

import ColumnResizer from './ColumnResizer';
import { pinnedLeftStyles, pinnedRightStyles, responsiveHeaderCellStyles } from './styles';

interface EnhancedHeaderCellProps {
  columnResizeDirection: ColumnResizeDirection;
  enableColumnResizing: boolean;
  header: Header<any, unknown>;
  isEditable: boolean;
  isLast: boolean;
  resizerOffset: number;
  sortDirection?: 'asc' | 'desc';
}

const EnhancedHeaderCell = ({
  columnResizeDirection,
  enableColumnResizing,
  header,
  isEditable,
  isLast,
  resizerOffset,
  sortDirection,
}: EnhancedHeaderCellProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <TableCell
      key={header.id}
      className={cx(
        responsiveHeaderCellStyles().className,
        ...(header.column.columnDef.meta?.headerClasses ?? []),
        header.column.getIsPinned() === 'left' ? pinnedLeftStyles().className : '',
        header.column.getIsPinned() === 'right' ? pinnedRightStyles().className : '',
      )}
      colSpan={header.colSpan}
      style={{
        width: header.id === 'selection' ? 30 : enableColumnResizing && header.getSize(),
        paddingLeft: isEditable ? 12 : undefined,
        zIndex: header.column.getIsPinned() && 100,
        borderRight: enableColumnResizing && isHovered ? '5px solid rgb(5,62,190)' : '',
      }}
      onMouseDown={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseOver={() => setIsHovered(true)}
    >
      {header.column.getCanSort() ? (
        <Grid alignItems="center" display="flex" justifyContent="space-between">
          <TableSortLabel
            key={header.id}
            active={header.column.getIsSorted() as boolean}
            className={responsiveHeaderCellStyles().className}
            direction={sortDirection}
            onClick={header.column.getToggleSortingHandler()}
          >
            <BodySmallBold>{flexRender(header.column.columnDef.header, header.getContext())}</BodySmallBold>
          </TableSortLabel>
          {enableColumnResizing && (
            <ColumnResizer
              deltaOffset={resizerOffset}
              isLast={isLast}
              resizeDirection={columnResizeDirection}
              onGetIsResizing={header.column.getIsResizing}
              onGetResizeHandler={header.getResizeHandler}
              onResetSize={header.column.resetSize}
            />
          )}
        </Grid>
      ) : (
        <BodySmallBold>{flexRender(header.column.columnDef.header, header.getContext())}</BodySmallBold>
      )}
    </TableCell>
  );
};

export default EnhancedHeaderCell;
